import type { Organization, PaginatedList, Patrol } from '@local/common-lib';
import {
  AddCircle as NewPatrolIcon,
  FlashOn as QuickPatrolIcon,
} from '@mui/icons-material';
import {
  Button,
  Card,
  CardActions,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { NavLink } from '../../components/nav-link/nav-link.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useHistoryState } from '../../hooks/use-history-state.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { ErrorPage } from '../error/index.js';
import { NarrowPatrolList } from './narrow-view.js';
import { NoScheduledPatrols } from './no-patrols.js';
import { WidePatrolList } from './wide-view.js';

export const ScheduledPatrolListPage: FC = () => {
  useSetTitle('Scheduled Patrols');
  const auth = useAuthenticated();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [pagination, setPagination] = useHistoryState('pagination', {
    limit: 12,
    skip: 0,
  });

  const [patrols] = useAxios<PaginatedList<Patrol.Type>>(
    '/api/v1/patrols',
    useMemo(
      () => ({
        params: {
          ...pagination,
          filter: JSON.stringify({
            deleted: null,
            when: { $gt: DateTime.now() },
          }),
          sort: '-when',
        },
      }),
      [pagination],
    ),
  );

  const [organization] = useAxios<Organization.Type>('/api/v1/my/organization');

  if (patrols.loading) {
    return <MiddleSpinner />;
  }

  if (patrols.error) {
    return <ErrorPage error={patrols.error} />;
  }

  if (!patrols.data.matches) {
    return <NoScheduledPatrols />;
  }

  const limited = !auth.userInfo.hasRole(
    'admin',
    'watcher',
    'summary-recipient',
  );
  const handleSelection = (id: string): unknown =>
    !limited && navigate(`/patrol/${id}`);

  return (
    <CenterLayout>
      <Stack spacing={3}>
        <Card>
          <CardActions sx={{ gap: 2, justifyContent: 'end' }}>
            {auth.userInfo.hasRole('patroller') &&
              organization.data?.allowQuickPatrol && (
                <Button
                  color="secondary"
                  component={NavLink}
                  startIcon={<QuickPatrolIcon />}
                  to="/patrol/quick-patrol"
                  variant="contained"
                >
                  Quick Patrol
                </Button>
              )}
            {auth.userInfo.hasRole('admin') && (
              <Button
                color="primary"
                component={NavLink}
                startIcon={<NewPatrolIcon />}
                to="/patrol/new"
                variant="outlined"
              >
                New Patrol
              </Button>
            )}
          </CardActions>
          {smallScreen ? (
            <NarrowPatrolList
              handleSelection={!limited ? handleSelection : undefined}
              hideWho={false}
              patrols={patrols.data}
            />
          ) : (
            <WidePatrolList
              handleSelection={!limited ? handleSelection : undefined}
              hideWho={false}
              patrols={patrols.data}
            />
          )}
        </Card>

        {patrols.data.matches > pagination.limit ? (
          <Pagination
            count={Math.ceil(patrols.data.matches / pagination.limit)}
            onChange={(_, page) => {
              setPagination({
                limit: pagination.limit,
                skip: pagination.limit * (page - 1),
              });
            }}
            page={Math.floor(pagination.skip / pagination.limit) + 1}
            showFirstButton
            showLastButton
            sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
          />
        ) : undefined}
      </Stack>
    </CenterLayout>
  );
};
